<template>
  <button
    type="button"
    class="plate"
    :class="[`path-${pathSlug}`, { 'is-selected': selected, 'is-disabled': disabled }]"
    :disabled="disabled && !selected"
    :aria-pressed="selected"
    @click="$emit('toggle', archetype.id)"
  >
    <img
      v-if="archetype.icon"
      class="plate__art"
      :src="archetype.icon"
      :alt="archetype.name"
      loading="lazy"
    />
    <span v-else class="plate__art plate__art--empty">{{ initials }}</span>

    <span class="plate__scrim"></span>

    <span
      v-if="archetype.warnings?.length"
      class="plate__warning"
      :title="archetype.warnings.join('\n')"
    >
      {{ archetype.warnings.length }}
    </span>

    <span class="plate__caption">
      <span class="plate__name">{{ archetype.name }}</span>
      <MetatypeBadge
        size="sm"
        :metatype="archetype.metatype"
        :runner-up="archetype.runner_up"
        :hybrid="archetype.is_hybrid"
      />
    </span>

    <span class="plate__path">{{ shortPath }}</span>
  </button>
</template>

<script setup>
import { computed } from 'vue';
import MetatypeBadge from './MetatypeBadge.vue';

const props = defineProps({
  archetype: { type: Object, required: true },
  selected: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});

defineEmits(['toggle']);

const initials = computed(() =>
  props.archetype.name
    .split(/[\s-]+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join(''),
);

const shortPath = computed(() => (props.archetype.path ?? '').replace('Path of ', ''));
const pathSlug = computed(() => shortPath.value.toLowerCase() || 'none');
</script>

<style scoped>
/* Twenty pieces of key art, each deliberately lit and each carrying its own
   accent colour, are the fastest identifier this tool has. So the art is the
   card: edge to edge, at its own 4:5 crop, with type sitting on it rather than
   beside it. A 3rem square thumbnail of near-monochrome art identifies nothing.
*/
.plate {
  position: relative;
  display: block;
  width: 100%;
  /* Height comes from the grid's row track, NOT from `aspect-ratio` here:
     Chromium ignores aspect-ratio on form controls, so a button sized that way
     collapses to the height of its own text and the art vanishes. */
  height: 100%;
  padding: 0;
  overflow: hidden;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  background: var(--gm-surface-sunken);
  border: 1px solid var(--gm-border-dim);
  border-left: 3px solid var(--path-color, var(--gm-border-dim));
  border-radius: var(--gm-radius);
  transition: border-color 0.15s ease, filter 0.15s ease;
}

/* The rail is the Path, not decoration: cool Flow for the Deviants, warm
   circuitry for the Technomancers, matching what the art itself glows. */
.plate.path-json { --path-color: var(--gm-info); }
.plate.path-john { --path-color: var(--gm-accent); }

.plate:hover:not(.is-disabled),
.plate:focus-visible {
  border-color: var(--gm-border);
  border-left-color: var(--path-color, var(--gm-border));
}

.plate:focus-visible {
  outline: 2px solid var(--gm-border-hi);
  outline-offset: 2px;
}

.plate.is-selected {
  border-color: var(--gm-border-hi);
}

.plate.is-selected::after {
  content: '';
  position: absolute;
  inset: 0;
  border: 1px solid var(--gm-border-hi);
  border-radius: calc(var(--gm-radius) - 1px);
  pointer-events: none;
}

.plate.is-disabled {
  filter: grayscale(1);
  opacity: 0.35;
  cursor: not-allowed;
}

.plate__art {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* The art is a waist-up 2:3 card with the head in the upper third. Biasing
     the crop upward keeps the face at plate size. */
  object-position: 50% 14%;
}

.plate__art--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--gm-font-display);
  font-size: 1.6rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--gm-text-dim);
  background: var(--gm-surface-1);
}

.plate__scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(12, 14, 15, 0.96) 0%,
    rgba(12, 14, 15, 0.82) 30%,
    rgba(12, 14, 15, 0.15) 62%,
    rgba(12, 14, 15, 0.35) 100%
  );
  pointer-events: none;
}

.plate__caption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: var(--gm-gap-sm);
}

.plate__name {
  font-family: var(--gm-font-display);
  font-size: 0.74rem;
  font-weight: 600;
  line-height: 1.25;
  letter-spacing: 0.02em;
  color: var(--gm-text);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
}

.plate__path {
  position: absolute;
  top: 0;
  left: 0;
  padding: 0.25rem 0.4rem;
  font-family: var(--gm-font-display);
  font-size: 0.52rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--path-color, var(--gm-text-dim));
  background: rgba(12, 14, 15, 0.7);
  border-bottom-right-radius: var(--gm-radius-sm);
}

.plate__warning {
  position: absolute;
  top: 0.35rem;
  right: 0.35rem;
  min-width: 1.05rem;
  height: 1.05rem;
  display: grid;
  place-items: center;
  font-size: 0.6rem;
  font-weight: 700;
  color: var(--gm-surface-0);
  background: var(--gm-accent-hi);
  border-radius: 50%;
}

@media (prefers-reduced-motion: reduce) {
  .plate {
    transition: none;
  }
}
</style>
